import { useCallback, useEffect, useMemo, useState } from 'react';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useCPTLabelSearch } from '../../hooks';
import { DependantLabels } from '../schema';
import { CPT_LABEL_PAGE_COUNT } from '..';

type UseClientChipData = {
  options: WbsLabelValueSearchResult[];
  handleSearch: (query: string) => void;
  label?: TimesheetLabel | null;
  loading: boolean;
  hasMore: boolean;
};

export const useClients = (
  dependentLabels: DependantLabels
): UseClientChipData => {
  const [options, setOptions] = useState<WbsLabelValueSearchResult[]>([]);
  const [lastSearch, setLastSearch] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const {
    searchHandler,
    data: { client: clientsData }
  } = useCPTLabelSearch();

  const loading = useMemo(() => Boolean(!clientsData), [clientsData]);

  const handleSearch = useCallback(
    (queryText: string) => {
      if (lastSearch !== queryText) {
        searchHandler({
          context: 'client',
          query: queryText || undefined
        });
      }
      setLastSearch(queryText);
    },
    [searchHandler, lastSearch, setLastSearch]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (clientsData) {
      const clients = clientsData?.wbsLabelValueSearch?.clients
        .items as WbsLabelValueSearchResult[];
      setOptions(clients);
      setHasMore(clients?.length === CPT_LABEL_PAGE_COUNT);
    }
  }, [clientsData, loading]);

  return useMemo(
    () => ({
      options,
      label: dependentLabels?.client?.label,
      handleSearch,
      loading,
      hasMore
    }),
    [options, dependentLabels?.client?.label, handleSearch, loading, hasMore]
  );
};

export default useClients;
